import { useEffect } from 'react';
import { ReactComponent as AddSvg } from '../images/add.svg';
import { ReactComponent as DeleteSvg } from '../images/delete.svg';
import { Chart, ChartMode } from '../pages/statistics';
import './chartSelector.css';
import { Options } from './selectButton';
import { SquaredIcon } from './squaredIcon';
import { formatDate } from '../pages/utils/utils';
import ReactApexChart from 'react-apexcharts';

interface ChartSelectorProps {
  value: Chart;
  setValue: (value: Chart) => void;
  generate: boolean;
  getData: (data: any, setChartData: (data: any) => void) => void;
}

export const options: Options[] = [
  {
    id: '0',
    name: 'Selección:'
  },
  {
    id: '1',
    name: 'Apertura/Cierre'
  },
  {
    id: '2',
    name: 'Agenda'
  },
  {
    id: '3',
    name: 'Materia Legal'
  },
  {
    id: '4',
    name: 'Por tipo: Civil'
  },
  {
    id: '5',
    name: 'Por tipo: Familiar'
  },
  {
    id: '6',
    name: 'Por tipo: Laboral'
  },
  {
    id: '7',
    name: 'Por tipo: Agrario'
  },
  {
    id: '8',
    name: 'Por tipo: Penal'
  },
  {
    id: '9',
    name: 'Por tipo: Amparo'
  }
];

export function ChartSelector({
  value,
  setValue,
  generate,
  getData
}: ChartSelectorProps) {
  useEffect(() => {
    getData(value, setChartData);
  }, [generate]);

  function setChartData(data: any) {
    setValue({ ...value, mode: ChartMode.chart, chartData: data });
  }

  function setSelectMode() {
    setValue({ ...value, mode: ChartMode.select });
  }

  function constructSelectMode() {
    return (
      <div className="chart-select-container">
        <p>
          <strong>Rango:</strong>
        </p>
        <div className="chart-select-date-inputs">
          <div className="log-new-item-input">
            <input
              type="date"
              className="agenda-input"
              id="dateStart"
              required
              onChange={(event) =>
                setValue({ ...value, dateStart: new Date(event.target.value) })
              }
            />
          </div>
          <div className="log-new-item-input">
            <input
              type="date"
              className="agenda-input"
              id="dateEnd"
              onChange={(event) =>
                setValue({ ...value, dateEnd: new Date(event.target.value) })
              }
            />
          </div>
        </div>
        <p>
          <strong>Selección:</strong>
        </p>
        <select
          id="idCourt"
          name="idCourt"
          className="proceedings-form-input proceedings-form-select"
          onChange={(event) => setValue({ ...value, type: event.target.value })}
        >
          {options.map((option: any) => (
            <option value={option.id}>
              <strong>{option.name}</strong>
            </option>
          ))}
        </select>
        <div className="delete-icon">
          <SquaredIcon
            icon={<DeleteSvg className="clickable" />}
            onClick={() => deleteSelection()}
          />
        </div>
      </div>
    );
  }

  function constructChartMode() {
    if (value.chartData && value.dateStart && value.dateEnd) {
      const offsetStartDate = new Date(
        value.dateStart.getTime() +
          Math.abs(value.dateStart.getTimezoneOffset() * 60000)
      );
      const offsetEndDate = new Date(
        value.dateEnd.getTime() +
          Math.abs(value.dateEnd.getTimezoneOffset() * 60000)
      );
      let chartJSX = <p>No hay información en el periodo escogido.</p>;
      const chartOptions: any = {
        chart: {
          id: 'chart' + value.id,
          width: 300,
          type: 'pie',
        },
        legend: {
          show: true,
          position: 'bottom',
          fontWeight: 700,
        },
        options: {
        }
      };
      let series: any = [];
      switch (value.type) {
        case '1':
          if (value.chartData[0].open && value.chartData[0].close) {
            series = [
              parseInt(value.chartData[0].open),
              parseInt(value.chartData[0].close)
            ];
            chartOptions['labels'] = [
              'Abiertos ' + value.chartData[0].open,
              'Cerrados ' + value.chartData[0].close
            ];
          }
          break;
        case '2':
          if (value.chartData[0].total) {
            chartJSX = (
              <div className="chart-appointments">
                <p>En el periodo seleccionado se atendieron:</p>
                <p style={{ fontSize: '30px' }}>
                  <strong>{value.chartData[0].total}</strong>
                </p>
                <p>Citas</p>
              </div>
            );
          }
          break;
        case '3':
          if (value.chartData.length > 0) {
            const labels: any = [];
            value.chartData.forEach((data: any) => {
              series.push(parseInt(data.total));
              labels.push(data.branch + ' ' + data.total);
            });
            chartOptions['labels'] = labels;
          }
          break;
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          if (value.chartData.length > 0) {
            const labels: any = [];
            value.chartData.forEach((data: any) => {
              series.push(parseInt(data.total));
              labels.push(data.type + ' ' + data.total);
            });
            chartOptions['labels'] = labels;
          }
          break;
        default:
          break;
      }

      if (series.length > 0) {
        chartJSX = (
          <ReactApexChart
            options={chartOptions}
            series={series}
            type="pie"
            width={chartOptions.chart.width}
          />
        );
      }
      return (
        <div className="chart-container">
          <p>
            {formatDate(offsetStartDate)} - {formatDate(offsetEndDate)}
          </p>
          <p>
            Selección:{' '}
            {options.find((option) => value.type === option.id)?.name}
          </p>
          {chartJSX}
          <div className="delete-icon">
          <DeleteSvg className="clickable" onClick={() => deleteSelection()}/>
          </div>
        </div>
      );
    }
  }

  function deleteSelection() {
    setValue({
      id: value.id,
      type: '',
      dateStart: null,
      dateEnd: null,
      mode: ChartMode.add,
      chartData: null
    });
  }

  /* if (!value.isFetchingData) {
    return <p>Obteniendo información...</p>;
  } */

  return (
    <>
      {value.mode == ChartMode.add && (
        <AddSvg className="agenda-add-icon" onClick={() => setSelectMode()} />
      )}
      {value.mode == ChartMode.select && constructSelectMode()}
      {value.mode == ChartMode.chart && constructChartMode()}
    </>
  );
}
